// src/services/ExportService.ts
import { Device } from '../context/DeviceContext';

export class ExportService {
  /**
   * Export devices to CSV format
   */
  static exportToCSV(devices: Device[], filename: string = 'voltfox-devices.csv'): void {
    if (devices.length === 0) {
      console.warn('No devices to export');
      return;
    }

    // CSV Header
    const headers = [
      'Name',
      'Type',
      'Icon',
      'Current Charge (%)',
      'Health (%)',
      'Status',
      'Voltage (V)',
      'Capacity (mAh)',
      'Chemistry',
      'Location',
      'Notes',
      'Purchase Price',
      'Purchase Date',
      'Warranty Until',
      'Created At',
      'Last Updated'
    ];

    // CSV Rows
    const rows = devices.map(device => [
      this.escapeCSV(device.name),
      this.escapeCSV(device.type),
      device.icon || '',
      device.currentCharge || 0,
      device.health || 100,
      device.status || 'healthy',
      device.voltage || '',
      device.capacity || '',
      device.chemistry || '',
      this.escapeCSV(device.storageLocation || ''),
      this.escapeCSV(device.notes || ''),
      device.purchasePrice || '',
      device.purchaseDate ? new Date(device.purchaseDate).toLocaleDateString() : '',
      device.warrantyUntil ? new Date(device.warrantyUntil).toLocaleDateString() : '',
      device.createdAt ? new Date(device.createdAt).toLocaleDateString() : '',
      device.updatedAt ? new Date(device.updatedAt).toLocaleDateString() : ''
    ]);

    // Create CSV content
    const csvContent = [
      headers.join(','),
      ...rows.map(row => row.join(','))
    ].join('\n');

    // Download CSV
    this.downloadFile(csvContent, filename, 'text/csv;charset=utf-8;');
  }

  /**
   * Export devices to Excel-compatible CSV (with UTF-8 BOM)
   */
  static exportToExcel(devices: Device[], filename: string = 'voltfox-devices.csv'): void {
    if (devices.length === 0) {
      console.warn('No devices to export');
      return;
    }

    // CSV Header
    const headers = [
      'Name',
      'Type',
      'Icon',
      'Current Charge (%)',
      'Health (%)',
      'Status',
      'Voltage (V)',
      'Capacity (mAh)',
      'Chemistry',
      'Location',
      'Notes',
      'Purchase Price',
      'Purchase Date',
      'Warranty Until',
      'Created At',
      'Last Updated'
    ];

    // CSV Rows
    const rows = devices.map(device => [
      this.escapeCSV(device.name),
      this.escapeCSV(device.type),
      device.icon || '',
      device.currentCharge || 0,
      device.health || 100,
      device.status || 'healthy',
      device.voltage || '',
      device.capacity || '',
      device.chemistry || '',
      this.escapeCSV(device.storageLocation || ''),
      this.escapeCSV(device.notes || ''),
      device.purchasePrice || '',
      device.purchaseDate ? new Date(device.purchaseDate).toLocaleDateString() : '',
      device.warrantyUntil ? new Date(device.warrantyUntil).toLocaleDateString() : '',
      device.createdAt ? new Date(device.createdAt).toLocaleDateString() : '',
      device.updatedAt ? new Date(device.updatedAt).toLocaleDateString() : ''
    ]);

    // Create CSV content with UTF-8 BOM for Excel
    const csvContent = '\uFEFF' + [
      headers.join(','),
      ...rows.map(row => row.join(','))
    ].join('\n');

    // Download CSV
    this.downloadFile(csvContent, filename, 'text/csv;charset=utf-8;');
  }

  /**
   * Export device history to CSV
   */
  static exportHistoryToCSV(
    deviceName: string,
    history: Array<{ date: string; charge: number; health?: number }>,
    filename?: string
  ): void {
    if (history.length === 0) {
      console.warn('No history to export');
      return;
    }

    const defaultFilename = `voltfox-${deviceName.toLowerCase().replace(/\s+/g, '-')}-history.csv`;
    const finalFilename = filename || defaultFilename;

    // CSV Header
    const headers = ['Date', 'Charge (%)', 'Health (%)'];

    // CSV Rows
    const rows = history.map(entry => [
      new Date(entry.date).toLocaleString(),
      entry.charge,
      entry.health || ''
    ]);

    // Create CSV content
    const csvContent = [
      headers.join(','),
      ...rows.map(row => row.join(','))
    ].join('\n');

    // Download CSV
    this.downloadFile(csvContent, finalFilename, 'text/csv;charset=utf-8;');
  }

  /**
   * Export devices to JSON format
   */
  static exportToJSON(devices: Device[], filename: string = 'voltfox-devices.json'): void {
    if (devices.length === 0) {
      console.warn('No devices to export');
      return;
    }

    const jsonContent = JSON.stringify(devices, null, 2);
    this.downloadFile(jsonContent, filename, 'application/json;charset=utf-8;');
  }

  /**
   * Escape CSV special characters
   */
  private static escapeCSV(value: string | number): string {
    if (typeof value !== 'string') {
      return String(value);
    }

    // If the value contains comma, newline, or double quote, wrap it in quotes
    if (value.includes(',') || value.includes('\n') || value.includes('"')) {
      // Escape double quotes by doubling them
      return `"${value.replace(/"/g, '""')}"`;
    }

    return value;
  }

  /**
   * Download file to user's device
   */
  private static downloadFile(content: string, filename: string, mimeType: string): void {
    const blob = new Blob([content], { type: mimeType });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');

    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    // Clean up the URL
    setTimeout(() => URL.revokeObjectURL(url), 100);
  }

  /**
   * Get export summary
   */
  static getExportSummary(devices: Device[]): {
    totalDevices: number;
    healthyDevices: number;
    warningDevices: number;
    criticalDevices: number;
    averageCharge: number;
    averageHealth: number;
  } {
    const totalDevices = devices.length;
    const healthyDevices = devices.filter(d => d.status === 'healthy').length;
    const warningDevices = devices.filter(d => d.status === 'warning').length;
    const criticalDevices = devices.filter(d => d.status === 'critical').length;

    const averageCharge =
      devices.reduce((sum, d) => sum + (d.currentCharge || 0), 0) / (totalDevices || 1);
    const averageHealth =
      devices.reduce((sum, d) => sum + (d.health || 100), 0) / (totalDevices || 1);

    return {
      totalDevices,
      healthyDevices,
      warningDevices,
      criticalDevices,
      averageCharge: Math.round(averageCharge),
      averageHealth: Math.round(averageHealth)
    };
  }
}
